/**
 * Customer Panel Error Handler
 * 
 * Merkezi error handling utility - OOP prensiplerine uygun
 * Tekrar eden error handling kodlarını önler
 */

export interface ErrorResponse {
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
}

/**
 * API response'dan kullanıcı dostu hata mesajı çıkarır
 * JSON formatındaki mesajları parse edip sadece message kısmını gösterir
 */
export function extractErrorMessage(error: unknown, fallback: string = 'An error occurred'): string {
  // Error instance ise direkt message'ı döndür
  if (error instanceof Error) {
    return cleanErrorMessage(error.message, fallback);
  }

  // String ise temizle
  if (typeof error === 'string') {
    return cleanErrorMessage(error, fallback);
  }

  return fallback;
}

/**
 * Hata mesajını temizle - JSON formatındaki mesajları parse edip sadece message kısmını göster
 */
function cleanErrorMessage(errorMessage: string, fallback: string): string {
  // Eğer mesaj zaten temiz bir string ise direkt döndür
  if (!errorMessage.includes('{') && !errorMessage.includes('API error:')) {
    return errorMessage;
  }

  // JSON parse etmeye çalış
  try {
    // "API error: 422 - {...}" formatındaki mesajları temizle
    const jsonMatch = errorMessage.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const errorData: ErrorResponse = JSON.parse(jsonMatch[0]);
      
      // Validation errors varsa ilk hatayı göster
      if (errorData.errors && Object.keys(errorData.errors).length > 0) {
        const firstErrorKey = Object.keys(errorData.errors)[0];
        const firstError = Array.isArray(errorData.errors[firstErrorKey]) 
          ? errorData.errors[firstErrorKey][0] 
          : errorData.errors[firstErrorKey];
        return firstError || errorData.message || errorData.error || fallback;
      }
      
      // Sadece message veya error kısmını döndür
      return errorData.message || errorData.error || fallback;
    }
    
    // JSON bulunamazsa, " - " sonrasındaki kısmı al
    if (errorMessage.includes(' - ')) {
      return errorMessage.split(' - ').pop() || fallback;
    }
  } catch {
    // JSON parse edilemezse, " - " sonrasındaki kısmı al veya orijinal mesajı döndür
    if (errorMessage.includes(' - ')) {
      return errorMessage.split(' - ').pop() || fallback;
    }
  }
  
  return errorMessage || fallback;
}

/**
 * Response'dan error mesajı çıkarır
 */
export async function extractErrorFromResponse(response: Response, fallback: string = 'Request failed'): Promise<string> {
  try {
    const text = await response.text();
    if (!text) {
      return fallback;
    }

    const errorData: ErrorResponse = JSON.parse(text);
    
    // Validation errors varsa ilk hatayı göster
    if (errorData.errors && Object.keys(errorData.errors).length > 0) {
      const firstErrorKey = Object.keys(errorData.errors)[0];
      const firstError = Array.isArray(errorData.errors[firstErrorKey]) 
        ? errorData.errors[firstErrorKey][0] 
        : errorData.errors[firstErrorKey];
      return firstError || errorData.message || errorData.error || fallback;
    }
    
    return errorData.message || errorData.error || fallback;
  } catch {
    return fallback;
  }
}

